import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ClockIcon } from '@heroicons/react/24/outline';

const WaitNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const formatDuration = (duration: number, unit: string) => {
    switch (unit) {
      case 'seconds': return `${duration}s`;
      case 'minutes': return `${duration}m`;
      case 'hours': return `${duration}h`;
      case 'days': return `${duration}d`;
      default: return `${duration} ${unit}`;
    }
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-emerald-400 to-emerald-500 border-2 transition-all duration-200 ${
      selected ? 'border-emerald-600 shadow-xl scale-105' : 'border-emerald-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ClockIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-emerald-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Config preview */}
          {data.config?.duration && data.config?.unit && (
            <div className="text-xs text-emerald-100 mt-1">
              Várakozás: {formatDuration(data.config.duration, data.config.unit)}
            </div>
          )}
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 
            data.executionData?.isWaiting ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution data preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-emerald-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="font-mono">
            {data.executionData.isWaiting ? (
              <>Várakozás... {data.executionData.remainingTime || 'ismeretlen'}</>
            ) : (
              <>Várakozás befejezve</>
            )}
          </div>
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="w-3 h-3 bg-white border-2 border-emerald-500"
        style={{ left: -6 }}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="w-3 h-3 bg-white border-2 border-emerald-500"
        style={{ right: -6 }}
      />
    </div>
  );
};

export default WaitNode;
